import { ref } from '@nuxtjs/composition-api';
import { defineStore } from 'pinia';

import { useCountrySearch } from '~/composables';
import { useApi } from '~/composables/useApi';
import storeAllowedCountriesList from '~/diptyqueTheme/customQueries/magento/storeAllowedCountriesList';
import {
  AllowedCountriesResponseInterface,
  CountryInterface,
  CountryStoreInterface,
  LocalStorageKeyType,
  TransformedCountryInterface
} from '~/diptyqueTheme/stores/countries/types';
import { Logger } from '~/helpers/logger';
import { lsGet, lsSet } from '~/diptyqueTheme/composable/useLocalStorage';

export const useCountriesStore = defineStore('countries', (): CountryStoreInterface => {
  const countries = ref<CountryInterface[]>([]);
  const allowedCountries = ref<TransformedCountryInterface[]>([]);
  const { load: loadCountries } = useCountrySearch();
  const { query } = useApi();

  const loadCountriesList = async (): Promise<CountryInterface[]> => {
    try {
      if (!lsGet('all_countries')) {
        countries.value = (await loadCountries()) as CountryInterface[];
        lsSet('all_countries', countries.value);
      } else {
        countries.value = getCountriesFromLocalStorage('all_countries') as CountryInterface[];
      }

      return countries.value;
    } catch (error) {
      Logger.error('loadCountriesList [ERROR]', error);
      console.error(error);
    }
  };

  const getCountryNameByCode = (code: string): string => {
    return countries.value?.find((country: CountryInterface) => country.id === code)?.full_name_locale;
  };

  const getAllowedCountries = async (): Promise<TransformedCountryInterface[]> => {
    try {
      if (!lsGet('allowed_countries')) {
        const { data } = await query(storeAllowedCountriesList);
        const countriesList = (data as AllowedCountriesResponseInterface)?.getAllowedCounries;
        if (countriesList?.length) {
          allowedCountries.value = countriesList.map((country) => ({
            id: country.id,
            label: country.full_name_locale,
            englishLabel: country.full_name_english,
            abbreviation: country.two_letter_abbreviation,
            availableRegions: country?.available_regions
          }));
          lsSet('allowed_countries', allowedCountries.value);
        } else {
          allowedCountries.value = getCountriesFromLocalStorage('allowed_countries') as TransformedCountryInterface[];
        }
      } else {
        allowedCountries.value = getCountriesFromLocalStorage('allowed_countries') as TransformedCountryInterface[];
      }
      return allowedCountries.value;
    } catch (error) {
      Logger.error('getAllowedCountries [ERROR]', error);
      console.error(error);
    }
  };

  const getCountriesFromLocalStorage = (key: LocalStorageKeyType): CountryInterface[] | TransformedCountryInterface[] => {
    return lsGet(key) || [];
  };

  return {
    countries,
    allowedCountries,
    loadCountriesList,
    getAllowedCountries,
    getCountryNameByCode,
    getCountriesFromLocalStorage
  };
});
